/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    // T26: Pending display never edits actual cart lines, quantities or totals.
    t26BeginAdd(opener) {
      this.t26PendingAdd = true;
      this.setAttribute('data-t26-pending-add', '');
      const status = this.querySelector('[data-t26-cart-pending]');
      const product = opener.closest('.js-product');
      const swatch = opener.closest('t26-product-swatch');
      const cutting = opener.closest('t26-cutting-service');
      const title = product?.querySelector('.product-title')?.textContent.trim() || '';
      const detail = swatch?.querySelector('h2')?.textContent.trim()
        || cutting?.querySelector('[data-t26-cutting-summary]')?.textContent.trim() || '';
      const variant = product?.querySelector('variant-picker')?.variant;
      const source = swatch?.querySelector('img')
        || (variant?.featured_media && product?.querySelector(`media-gallery .media-viewer [data-media-id="${variant.featured_media.id}"] img`));
      if (status) {
        status.querySelector('[data-t26-cart-pending-title]').textContent = [title, detail].filter(Boolean).join(' · ');
        status.querySelector('[data-t26-cart-pending-label]').textContent = this.dataset.t26CartAdding;
        const img = status.querySelector('[data-t26-cart-pending-image]');
        img.hidden = !(source?.complete && source.naturalWidth);
        if (!img.hidden) img.src = source.currentSrc || source.src;
        else img.removeAttribute('src');
        status.hidden = false;
      }
      const error = this.querySelector('[data-t26-cart-error]');
      if (error) error.hidden = true;
      this.t26SetBusy(true);
      this.open(opener);
    }

    t26SetBusy(busy) {
      this.toggleAttribute('aria-busy', busy);
      if (busy) this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('.drawer__content, .cart-drawer__summary').forEach((el) => { el.inert = busy; });
    }

    t26FinishAdd(error = '', unresolved = false) {
      this.t26PendingAdd = false;
      this.removeAttribute('data-t26-pending-add');
      const status = this.querySelector('[data-t26-cart-pending]');
      if (status) status.hidden = true;
      const alert = this.querySelector('[data-t26-cart-error]');
      if (alert) {
        alert.querySelector('[data-t26-cart-error-text]').textContent = error;
        alert.hidden = !error;
      }
      this.t26SetBusy(unresolved);
      if (this.hasAttribute('open')) trapFocus(this);
    }

    // T26: A failed Add may still have changed Shopify's cart. Reconcile with GET only.
    async t26RecoverCart() {
      const ids = [...new Set([...this.getSectionsToRender().map((section) => section.section), 'cart-json'])];
      const response = await fetch(`?sections=${ids.join(',')}`);
      if (!response.ok) throw new Error('Cart refresh failed');
      const sections = await response.json();
      const template = document.createElement('template');
      template.innerHTML = sections['cart-json'] || '';
      const state = template.content.querySelector('[data-cart-state]');
      let cart;
      try { cart = state && JSON.parse(state.textContent); } catch (_) { /* T26: Fall back to cart JSON below. */ }
      if (!Array.isArray(cart?.items)) {
        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!cartResponse.ok) throw new Error('Cart state unavailable');
        cart = await cartResponse.json();
        if (!Array.isArray(cart?.items)) throw new Error('Invalid cart state');
      }
      theme.cart = { ...theme.cart, items: cart.items.map((item) => ({ variantId: item.variant_id, quantity: item.quantity })) };
      document.querySelectorAll('t26-product-swatch').forEach((swatch) => swatch.t26HandleCartEvent?.({ detail: { cart } }));
      this.renderContents({ sections }, false);
      return cart;
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      if (this.hasAttribute('open')) return;
      clearTimeout(this.t26OpenTimeout);
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // T26: Only Quick Add needs a transition delay; ordinary cart opens are immediate.
      const openDrawer = () => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      };
      if (quickAddDrawer) this.t26OpenTimeout = setTimeout(openDrawer, 200);
      else openDrawer();
    }

    close(callback) {
      clearTimeout(this.t26OpenTimeout);
      this.overlay.style.transitionDelay = '';
      super.close(callback);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      if (theme.t26CartMutation?.owner) return;
      const t26Revision = theme.t26CartMutation?.revision;
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          if (t26Revision !== theme.t26CartMutation?.revision) return;
          if (!cartResponse.ok) throw new Error('Cart refresh failed');
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    renderContents(data, openDrawer = true) {
      // T26: Validate all sections before replacing any live cart content.
      const updates = this.getSectionsToRender().map((section) => ({
        section,
        html: CartDrawer.getElementHTML(data.sections?.[section.section], section.selector)
      }));
      const pending = this.t26PendingAdd && this.querySelector('[data-t26-cart-pending]');
      updates.forEach(({ section, html }) => {
        const el = document.getElementById(section.id);
        if (el) el.innerHTML = html;
      });
      // T26: Reuse the already-loaded product image when Shopify returns the same file.
      // Its cart-sized URL would otherwise start a second download after Add completes.
      const cachedImage = pending && pending.querySelector('[data-t26-cart-pending-image]:not([hidden])');
      if (cachedImage?.complete && cachedImage.naturalWidth) {
        const imageFile = (src) => {
          const url = new URL(src, window.location.href);
          url.searchParams.delete('width');
          return url.href;
        };
        this.querySelectorAll('.cart-item img').forEach((img) => {
          if (imageFile(img.src) !== imageFile(cachedImage.src)) return;
          img.removeAttribute('srcset');
          img.src = cachedImage.src;
        });
      }
      if (pending) this.querySelector('[data-t26-cart-pending]')?.replaceWith(pending);
      this.t26SetBusy(Boolean(theme.t26CartMutation?.owner));

      this.cartSummary = this.querySelector('.cart-drawer__summary');

      if (openDrawer && this.getAttribute('open') === null) {
        this.open();
      } else if (this.getAttribute('open') !== null) {
        // Refresh the focusable element list after replacing the open drawer's contents.
        trapFocus(this);
      }

      // Cart controls enhance already-rendered markup, so they should never delay the drawer.
      this.ensureScriptsLoaded().catch((error) => console.log(error)); // eslint-disable-line
    }

    /**
     * Ensures scripts used by interactive cart controls are available.
     * @returns {Promise}
     */
    ensureScriptsLoaded() {
      if (this.scriptsLoaded) return Promise.resolve();
      if (this.scriptsLoadingPromise) return this.scriptsLoadingPromise;

      const scriptPromises = [];
      if (!customElements.get('cart-items')) {
        scriptPromises.push(loadScript(theme.scripts.cartItems));
      }

      if (this.dataset.shippingCalculator === 'true') {
        if (!customElements.get('country-province-selector')) {
          scriptPromises.push(loadScript(theme.scripts.countryProvinceSelector));
        }
        if (!customElements.get('shipping-calculator')) {
          scriptPromises.push(loadScript(theme.scripts.shippingCalculator));
        }
      }

      this.scriptsLoadingPromise = Promise.all(scriptPromises)
        .then(() => {
          this.scriptsLoaded = true;
        })
        .catch((error) => {
          this.scriptsLoadingPromise = null;
          throw error;
        });

      return this.scriptsLoadingPromise;
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      if (typeof html !== 'string') throw new Error('Missing cart section');
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const element = tmpl.content.querySelector(selector);
      if (!element) throw new Error('Invalid cart section');
      return element.innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
